/* eslint-disable @typescript-eslint/no-explicit-any */
import jsep from 'jsep';
import { JSONPath } from 'jsonpath-plus';

// Type definitions for better type safety
interface EvaluationContext {
  [key: string]: any;
}

interface EvaluationOptions {
  context?: EvaluationContext;
  data?: any;
}

type ExpressionValue = string | number | boolean | null | undefined;

// Extended types for expressions that jsep supports but doesn't export types for
interface LogicalExpressionNode extends jsep.Expression {
  type: 'LogicalExpression';
  operator: '&&' | '||';
  left: jsep.Expression;
  right: jsep.Expression;
}

interface ConditionalExpressionNode extends jsep.Expression {
  type: 'ConditionalExpression';
  test: jsep.Expression;
  consequent: jsep.Expression;
  alternate: jsep.Expression;
}

// Custom error class for expression evaluation errors
export class ExpressionEvaluationError extends Error {
  constructor(
    message: string,
    public expression: string,
    public cause?: Error,
  ) {
    super(
      `Expression evaluation error: ${message} in expression "${expression}"`,
    );
    this.name = 'ExpressionEvaluationError';
  }
}

/**
 * Evaluates expressions within {{ }} brackets
 * Supports:
 * - Basic arithmetic operations (+, -, *, /, %, **)
 * - Comparison operations (==, !=, <, >, <=, >=)
 * - Logical operations (&&, ||, !)
 * - JSONPath expressions ($.data, $.user.name, etc.)
 * - Variable references ($variable)
 * - Literals (numbers, strings, booleans)
 */
export function evaluateExpression(
  template: string,
  options: EvaluationOptions = {},
): string | any {
  const { context = {}, data = {} } = options;

  // Regular expression to match {{ expression }} patterns
  const expressionRegex = /\{\{\s*(.*?)\s*\}\}/g;

  // Check if the template is a single expression (exactly one complete {{ }} pattern)
  const trimmedTemplate = template.trim();
  const expressionMatches = trimmedTemplate.match(/\{\{\s*[^}]+\s*\}\}/g);

  if (
    expressionMatches &&
    expressionMatches.length === 1 &&
    trimmedTemplate.match(/^\{\{\s*[^}]+\s*\}\}$/)
  ) {
    // Single expression - return raw result
    const expression = trimmedTemplate
      .replace(/^\{\{\s*/, '')
      .replace(/\s*\}\}$/, '');
    try {
      const result = evaluateSingleExpression(expression.trim(), context, data);
      return result;
    } catch (error) {
      throw new ExpressionEvaluationError(
        error instanceof Error ? error.message : 'Unknown error',
        expression.trim(),
        error instanceof Error ? error : undefined,
      );
    }
  }

  // Multiple expressions or mixed content - convert to string as before
  return template.replace(expressionRegex, (match, expression) => {
    try {
      const result = evaluateSingleExpression(expression.trim(), context, data);
      return String(result ?? '');
    } catch (error) {
      throw new ExpressionEvaluationError(
        error instanceof Error ? error.message : 'Unknown error',
        expression,
        error instanceof Error ? error : undefined,
      );
    }
  });
}

/**
 * Evaluates a single expression string
 */
function evaluateSingleExpression(
  expression: string,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  if (!expression) {
    return '';
  }

  try {
    // Parse the expression using jsep
    const ast = jsep(expression);
    return evaluateASTNode(ast, context, data);
  } catch (error) {
    throw new Error(
      `Failed to parse expression: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Recursively evaluates an AST node
 */
function evaluateASTNode(
  node: jsep.Expression,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  switch (node.type) {
    case 'Literal':
      return (node as jsep.Literal).value as ExpressionValue;

    case 'Identifier':
      return evaluateIdentifier((node as jsep.Identifier).name, context, data);

    case 'MemberExpression':
      return evaluateMemberExpression(
        node as jsep.MemberExpression,
        context,
        data,
      );

    case 'BinaryExpression':
      return evaluateBinaryExpression(
        node as jsep.BinaryExpression,
        context,
        data,
      );

    case 'UnaryExpression':
      return evaluateUnaryExpression(
        node as jsep.UnaryExpression,
        context,
        data,
      );

    case 'ConditionalExpression':
      return evaluateConditionalExpression(
        node as ConditionalExpressionNode,
        context,
        data,
      );

    case 'LogicalExpression':
      return evaluateLogicalExpression(
        node as LogicalExpressionNode,
        context,
        data,
      );

    case 'CallExpression':
      return evaluateCallExpression(node as jsep.CallExpression, context, data);

    default:
      throw new Error(`Unsupported expression type: ${node.type}`);
  }
}

/**
 * Evaluates identifier nodes (variables and JSONPath expressions)
 */
function evaluateIdentifier(
  name: string,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  // Handle JSONPath expressions starting with $
  if (name.startsWith('$')) {
    if (name === '$') {
      return data;
    }

    try {
      const jsonPath = name.replace(/^\$/, '$.');
      const result = JSONPath({ path: jsonPath, json: data });
      return result.length === 1 ? result[0] : result;
    } catch (error) {
      // If JSONPath fails, try to get from context
      return context[name];
    }
  }

  // Regular variable lookup
  return context[name];
}

/**
 * Evaluates member expressions (object.property)
 */
function evaluateMemberExpression(
  node: jsep.MemberExpression,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const object = evaluateASTNode(node.object, context, data);

  if (object == null) {
    return undefined;
  }

  let property: string | number;
  if (node.computed) {
    // Bracket notation: object[property]
    property = evaluateASTNode(node.property, context, data) as string | number;
  } else {
    // Dot notation: object.property
    property = (node.property as jsep.Identifier).name;
  }

  return (object as any)[property];
}

/**
 * Evaluates binary expressions (+, -, *, /, etc.)
 */
function evaluateBinaryExpression(
  node: jsep.BinaryExpression,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const left = evaluateASTNode(node.left, context, data);
  const right = evaluateASTNode(node.right, context, data);

  switch (node.operator) {
    case '+':
      return (left as any) + (right as any);
    case '-':
      return (left as any) - (right as any);
    case '*':
      return (left as any) * (right as any);
    case '/':
      return (left as any) / (right as any);
    case '%':
      return (left as any) % (right as any);
    case '**':
      return (left as any) ** (right as any);
    case '==':
      return left == right;
    case '!=':
      return left != right;
    case '===':
      return left === right;
    case '!==':
      return left !== right;
    case '<':
      return (left as any) < (right as any);
    case '<=':
      return (left as any) <= (right as any);
    case '>':
      return (left as any) > (right as any);
    case '>=':
      return (left as any) >= (right as any);
    case '&&':
      return (left as any) && (right as any);
    case '||':
      return (left as any) || (right as any);
    default:
      throw new Error(`Unsupported binary operator: ${node.operator}`);
  }
}

/**
 * Evaluates unary expressions (!, -, +)
 */
function evaluateUnaryExpression(
  node: jsep.UnaryExpression,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const argument = evaluateASTNode(node.argument, context, data);

  switch (node.operator) {
    case '!':
      return !argument;
    case '-':
      return -(argument as any);
    case '+':
      return +(argument as any);
    default:
      throw new Error(`Unsupported unary operator: ${node.operator}`);
  }
}

/**
 * Evaluates logical expressions (&&, ||)
 */
function evaluateLogicalExpression(
  node: LogicalExpressionNode,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const left = evaluateASTNode(node.left, context, data);

  switch (node.operator) {
    case '&&':
      return left && evaluateASTNode(node.right, context, data);
    case '||':
      return left || evaluateASTNode(node.right, context, data);
    default:
      throw new Error(`Unsupported logical operator: ${node.operator}`);
  }
}

/**
 * Evaluates conditional expressions (ternary operator)
 */
function evaluateConditionalExpression(
  node: ConditionalExpressionNode,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const test = evaluateASTNode(node.test, context, data);
  return test
    ? evaluateASTNode(node.consequent, context, data)
    : evaluateASTNode(node.alternate, context, data);
}

/**
 * Evaluates function calls (basic math functions)
 */
function evaluateCallExpression(
  node: jsep.CallExpression,
  context: EvaluationContext,
  data: any,
): ExpressionValue {
  const callee = node.callee as jsep.Identifier;
  const args = node.arguments.map((arg) => evaluateASTNode(arg, context, data));

  // Built-in Math functions
  const mathFunctions: { [key: string]: (...args: any[]) => any } = {
    abs: Math.abs,
    ceil: Math.ceil,
    floor: Math.floor,
    round: Math.round,
    max: Math.max,
    min: Math.min,
    pow: Math.pow,
    sqrt: Math.sqrt,
  };

  // Built-in functions
  const builtinFunctions: { [key: string]: (...args: any[]) => any } = {
    ...mathFunctions,
    String,
    Number,
    Boolean,
  };

  // Handle Math.function calls
  if (node.callee.type === 'MemberExpression') {
    const memberExpr = node.callee as jsep.MemberExpression;
    const objectName = (memberExpr.object as jsep.Identifier).name;
    const propertyName = (memberExpr.property as jsep.Identifier).name;

    if (objectName === 'Math' && propertyName in mathFunctions) {
      return mathFunctions[propertyName](...args);
    }
  }

  // Check if it's a built-in function
  if (callee.name in builtinFunctions) {
    return builtinFunctions[callee.name](...args);
  }

  // Check context for custom functions
  if (callee.name in context && typeof context[callee.name] === 'function') {
    return context[callee.name](...args);
  }

  throw new Error(`Unknown function: ${callee.name}`);
}

// Example usage and helper functions
export function createEvaluator(defaultContext: EvaluationContext = {}) {
  return (template: string, options: Partial<EvaluationOptions> = {}) => {
    return evaluateExpression(template, {
      context: { ...defaultContext, ...options.context },
      data: options.data,
    });
  };
}

// Example usage:
/*
const data = {
  user: { name: 'John', age: 30 },
  items: [1, 2, 3, 4, 5],
  config: { multiplier: 2 }
};

const context = {
  a: 10,
  b: 5,
  greeting: 'Hello'
};

// Examples:
console.log(evaluateExpression('{{ $.user.name }}', { data })); // "John"
console.log(evaluateExpression('{{ $a + $b }}', { context, data })); // "15"
console.log(evaluateExpression('{{ $a * $.config.multiplier }}', { context, data })); // "20"
console.log(evaluateExpression('{{ $.user.age > 25 ? "Adult" : "Young" }}', { data })); // "Adult"
console.log(evaluateExpression('{{ $greeting }}, {{ $.user.name }}!', { context, data })); // "Hello, John!"
*/
